import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './auth/AuthContext'
import { CookieBanner } from './components/CookieBanner'
import { ErrorBoundary } from './components/ErrorBoundary'
import { InstallPrompt } from './components/InstallPrompt'
import { PageLoader } from './components/PageLoader'
import { TotpEnforcementGate } from './components/profile/TotpEnforcementGate'

// ── Eager: общедоступные + часто посещаемые страницы ────────────────────
import { LandingPage } from './pages/LandingPage'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { ClassesPage } from './pages/ClassesPage'
import { StudentsPage } from './pages/StudentsPage'
import { JournalPage } from './pages/JournalPage'
import { AttendancePage } from './pages/AttendancePage'
import { SchedulePage } from './pages/SchedulePage'
import { SubjectsPage } from './pages/SubjectsPage'
import { ProfilePage } from './pages/ProfilePage'
import { SchoolPage } from './pages/SchoolPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { ChatPage, ParentChatPage } from './pages/ChatPage'
import { ParentDashboard } from './pages/ParentDashboard'
import { ParentChildPage } from './pages/ParentChildPage'
import { ParentRegisterPage } from './pages/ParentRegisterPage'

// ── Lazy: редко используемые / тяжёлые страницы ─────────────────────────
// Public-страницы политик
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })))
const ConsentPage = lazy(() => import('./pages/ConsentPage').then((m) => ({ default: m.ConsentPage })))

// Тяжёлые редкие
const ImportPage = lazy(() => import('./pages/ImportPage').then((m) => ({ default: m.ImportPage })))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })))
const DocumentsPage = lazy(() => import('./pages/DocumentsPage').then((m) => ({ default: m.DocumentsPage })))
const TemplatesPage = lazy(() => import('./pages/TemplatesPage').then((m) => ({ default: m.TemplatesPage })))
const BillingPage = lazy(() => import('./pages/BillingPage').then((m) => ({ default: m.BillingPage })))
const AuditPage = lazy(() => import('./pages/AuditPage').then((m) => ({ default: m.AuditPage })))
const SuperAdminBillingPage = lazy(() => import('./pages/SuperAdminBillingPage').then((m) => ({ default: m.SuperAdminBillingPage })))

// LLM-tools (12 страниц, каждая со своим prompt-builder'ом)
const LessonPlanPage = lazy(() => import('./pages/LessonPlanPage').then((m) => ({ default: m.LessonPlanPage })))
const QuizPage = lazy(() => import('./pages/QuizPage').then((m) => ({ default: m.QuizPage })))
const TextLevelerPage = lazy(() => import('./pages/TextLevelerPage').then((m) => ({ default: m.TextLevelerPage })))
const RubricPage = lazy(() => import('./pages/RubricPage').then((m) => ({ default: m.RubricPage })))
const CharacteristicPage = lazy(() => import('./pages/CharacteristicPage').then((m) => ({ default: m.CharacteristicPage })))
const ParentLetterPage = lazy(() => import('./pages/ParentLetterPage').then((m) => ({ default: m.ParentLetterPage })))
const WorksheetPage = lazy(() => import('./pages/WorksheetPage').then((m) => ({ default: m.WorksheetPage })))
const SelfEducationPage = lazy(() => import('./pages/SelfEducationPage').then((m) => ({ default: m.SelfEducationPage })))
const ClassPortraitPage = lazy(() => import('./pages/ClassPortraitPage').then((m) => ({ default: m.ClassPortraitPage })))
const QuarterReportPage = lazy(() => import('./pages/QuarterReportPage').then((m) => ({ default: m.QuarterReportPage })))
const TopicAnalysisPage = lazy(() => import('./pages/TopicAnalysisPage').then((m) => ({ default: m.TopicAnalysisPage })))
const VisionPage = lazy(() => import('./pages/VisionPage').then((m) => ({ default: m.VisionPage })))
const HomeworkPage = lazy(() => import('./pages/HomeworkPage').then((m) => ({ default: m.HomeworkPage })))
const BehaviorPage = lazy(() => import('./pages/BehaviorPage').then((m) => ({ default: m.BehaviorPage })))
const EssayCheckPage = lazy(() => import('./pages/EssayCheckPage').then((m) => ({ default: m.EssayCheckPage })))
const NotebookPage = lazy(() => import('./pages/NotebookPage').then((m) => ({ default: m.NotebookPage })))

/**
 * Routing:
 *   /                     — LandingPage (public, всегда)
 *   /login                — LoginPage (если не залогинен)
 *   /app                  — Dashboard (требует auth, иначе → /login)
 *   /app/tools/*          — LLM-инструменты (требуют auth)
 *
 * Logout сбрасывает на /, авторизация перенаправляет на /app.
 */
export function App() {
  const { teacher, loading } = useAuth()
  const { pathname } = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // Хелперы для редиректов: PARENT → /parent, остальные → /app
  const homeForRole = () => (teacher?.role === 'PARENT' ? '/parent' : '/app')
  const requireTeacher = (node: ReactNode) =>
    !teacher ? <Navigate to="/login" replace />
      : teacher.role === 'PARENT' ? <Navigate to="/parent" replace />
      : <TotpEnforcementGate>{node}</TotpEnforcementGate>
  const requireParent = (node: ReactNode) =>
    !teacher ? <Navigate to="/login" replace />
      : teacher.role !== 'PARENT' ? <Navigate to="/app" replace />
      : node

  return (
    <>
    {/* Ошибка рендера/загрузки чанка одной страницы не роняет всё приложение;
        переход на другой маршрут сбрасывает состояние ошибки. */}
    <ErrorBoundary resetKey={pathname}>
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/consent" element={<ConsentPage />} />
      <Route path="/login" element={teacher ? <Navigate to={homeForRole()} replace /> : <LoginPage />} />
      <Route path="/parent-register" element={<ParentRegisterPage />} />

      {/* Parent routes */}
      <Route path="/parent" element={requireParent(<ParentDashboard />)} />
      <Route path="/parent/child/:studentId" element={requireParent(<ParentChildPage />)} />
      <Route path="/parent/chat" element={requireParent(<ParentChatPage />)} />

      {/* Authenticated app (учитель / завуч / директор) */}
      <Route path="/app" element={requireTeacher(<DashboardPage />)} />
      <Route path="/app/classes" element={requireTeacher(<ClassesPage />)} />
      <Route path="/app/students" element={requireTeacher(<StudentsPage />)} />
      <Route path="/app/import" element={requireTeacher(<ImportPage />)} />
      <Route path="/app/subjects" element={requireTeacher(<SubjectsPage />)} />
      <Route path="/app/journal" element={requireTeacher(<JournalPage />)} />
      <Route path="/app/attendance" element={requireTeacher(<AttendancePage />)} />
      <Route path="/app/schedule" element={requireTeacher(<SchedulePage />)} />
      <Route path="/app/documents" element={requireTeacher(<DocumentsPage />)} />
      <Route path="/app/analytics" element={requireTeacher(<AnalyticsPage />)} />
      <Route path="/app/templates" element={requireTeacher(<TemplatesPage />)} />
      <Route path="/app/school" element={requireTeacher(<SchoolPage />)} />
      <Route path="/app/billing" element={requireTeacher(<BillingPage />)} />
      <Route path="/app/super-admin/billing" element={requireTeacher(<SuperAdminBillingPage />)} />
      <Route path="/app/chat" element={requireTeacher(<ChatPage />)} />
      <Route path="/app/audit" element={requireTeacher(<AuditPage />)} />
      <Route path="/app/notifications" element={requireTeacher(<NotificationsPage />)} />
      <Route path="/app/profile" element={requireTeacher(<ProfilePage />)} />
      <Route path="/app/tools/lesson-plan" element={requireTeacher(<LessonPlanPage />)} />
      <Route path="/app/tools/quiz" element={requireTeacher(<QuizPage />)} />
      <Route path="/app/tools/text-leveler" element={requireTeacher(<TextLevelerPage />)} />
      <Route path="/app/tools/rubric" element={requireTeacher(<RubricPage />)} />
      <Route path="/app/tools/characteristic" element={requireTeacher(<CharacteristicPage />)} />
      <Route path="/app/tools/parent-letter" element={requireTeacher(<ParentLetterPage />)} />
      <Route path="/app/tools/worksheet" element={requireTeacher(<WorksheetPage />)} />
      <Route path="/app/tools/self-education" element={requireTeacher(<SelfEducationPage />)} />
      <Route path="/app/tools/class-portrait" element={requireTeacher(<ClassPortraitPage />)} />
      <Route path="/app/tools/quarter-report" element={requireTeacher(<QuarterReportPage />)} />
      <Route path="/app/tools/topic-analysis" element={requireTeacher(<TopicAnalysisPage />)} />
      <Route path="/app/tools/vision" element={requireTeacher(<VisionPage />)} />
      <Route path="/app/tools/essay-check" element={requireTeacher(<EssayCheckPage />)} />
      <Route path="/app/tools/notebook" element={requireTeacher(<NotebookPage />)} />
      <Route path="/app/homework" element={requireTeacher(<HomeworkPage />)} />
      <Route path="/app/behavior" element={requireTeacher(<BehaviorPage />)} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
    <CookieBanner />
    {teacher && <InstallPrompt />}
    </>
  )
}
